/**
 * Hook: fetch the session's participants list and return an
 * identity-friendly lookup map for in-room UI use.
 *
 * Phase 1B-H-multi (UX). Used by `RoomLayout` to render role badges
 * on each tile so the operator can tell Field from Observer from
 * Auditor at a glance. The backend's identity scheme is
 * `participant:<uuid>` for non-operator participants — given a uuid,
 * `byParticipantId[uuid]` returns the role slug ("field" / "observer"
 * / "auditor").
 *
 * `byParticipantIdName` (backlog §9.4, 2026-09-08) additionally
 * surfaces each participant's `display_name`, keyed the same way —
 * used by `FieldParticipantPicker` to label a multi-field-participant
 * session's target picker with real names instead of LiveKit's
 * `participant:<uuid>` identity (LiveKit's own `.name` is always set
 * to the raw identity string by the token-minting service, so it's
 * useless for a human-facing label).
 *
 * Cheap polling at 30s: participants list changes (joins / leaves)
 * are infrequent enough that real-time sync isn't worth the websocket
 * complexity. The badge being a few seconds stale is fine.
 */
import { useQuery } from "@tanstack/react-query";

import { ApiPaths, api } from "./api";

type ParticipantRow = {
  id: string;
  role: "field" | "observer" | "auditor";
  display_name?: string | null;
};

type ParticipantsResponse =
  | ParticipantRow[]
  | { results: ParticipantRow[] };

export type ParticipantRoleLookup = {
  byParticipantId: Record<string, "field" | "observer" | "auditor">;
  byParticipantIdName: Record<string, string>;
};

export function useParticipantRoleLookup(
  sessionId: string | undefined,
): ParticipantRoleLookup {
  const q = useQuery({
    queryKey: ["session-participants-roles", sessionId],
    enabled: !!sessionId,
    refetchInterval: 30_000,
    queryFn: async () => {
      const res = await api.get<ParticipantsResponse>(
        ApiPaths.sessionParticipants(sessionId!),
      );
      return Array.isArray(res.data) ? res.data : res.data.results;
    },
  });

  const rows = q.data ?? [];
  const byParticipantId: Record<string, "field" | "observer" | "auditor"> = {};
  const byParticipantIdName: Record<string, string> = {};
  for (const r of rows) {
    if (r && r.id && r.role) {
      byParticipantId[r.id] = r.role;
    }
    if (r && r.id && r.display_name) {
      byParticipantIdName[r.id] = r.display_name;
    }
  }
  return { byParticipantId, byParticipantIdName };
}
